<script lang="ts" setup>
  // Validation occurs:
  // - Validate on Change/Blur initially (when the user leaves the control), let’s call this lazy mode.
  // - If the field is invalid, switch the validation to validate on input (when the user types), let’s call this aggressive mode.
  // - If the field is valid, go back to “lazy” mode, otherwise, be “aggressive”.
  // See https://vee-validate.logaretm.com/v4/guide/composition-api/custom-inputs#handling-events

  import DatePicker, { type DatePickerBlurEvent } from 'primevue/datepicker'
  import { camelCaseToKebabCase } from '~/utils/string'

  const props = withDefaults(
    defineProps<{
      disabled?: boolean
      label: string
      helperText?: string
      hourFormat?: '12' | '24'
      name: string
      minDate?: Date
      maxDate?: Date
      validateInitialValue?: boolean
    }>(),
    {
      disabled: false,
      helperText: undefined,
      hourFormat: '12',
      minDate: undefined,
      maxDate: undefined
    }
  )

  const emits = defineEmits<{
    (event: 'blur', payload: Event): void
  }>()

  const inputElement = ref<InstanceType<typeof DatePicker> | null>(null)
  defineExpose({ inputElement })

  const selectorName = computed(() => camelCaseToKebabCase(props.name))

  const { value, errorMessage, handleBlur, validate, meta } = useField<Date | string | null | undefined>(
    () => props.name,
    undefined,
    {
      validateOnValueUpdate: true
    }
  )

  onMounted(() => {
    // If there is an initial value on mount, and the field is not validated, validate it.
    if (props.validateInitialValue && !(value.value === undefined || value.value === null) && !meta.validated) {
      validate()
    }
  })

  const displayDate = computed({
    get() {
      return value.value ? new Date(value.value) : null
    },
    set(newValue: Date | null) {
      value.value = newValue ? newValue.toISOString() : null
    }
  })

  function onBlur(event: DatePickerBlurEvent) {
    emits('blur', event.originalEvent)
    handleBlur(event.originalEvent, true)
  }
</script>

<template>
  <div class="c-date-picker-field">
    <FloatLabel variant="in">
      <DatePicker
        :id="selectorName"
        v-model="displayDate"
        :class="{ 'p-invalid': errorMessage }"
        :disabled
        :hour-format="hourFormat"
        :max-date="maxDate"
        :min-date="minDate"
        :pt="{
          root: {
            class: 'mb-2'
          }
        }"
        date-format="dd/mm/yy,"
        fluid
        icon-display="input"
        show-icon
        show-time
        @blur="onBlur"
      />
      <label for="schedule-datetime">{{ label }}</label>
    </FloatLabel>
    <small v-if="errorMessage" :id="`${selectorName}-error`" class="input-error-message">
      {{ errorMessage }}
    </small>
    <small v-else-if="helperText" class="input-helper">{{ helperText }}</small>
  </div>
</template>

<style lang="scss" scoped>
  @import '../styles/form.scss';

  .input-helper {
    text-align: left;
    margin-left: 1px;
  }
</style>
